<template>
  <div class="flex flex-col w-full">
    <Toast position="top-[48px]"></Toast>
    <div class="text-[2rem] leading-[2.5rem] font-black text-[#433D31] tracking-[-0.03em]">
      Reset Email
    </div>
    <div class="text-[1rem] leading-[1.5rem] text-[#433D31] font-semibold pt-[8px]">
      Please enter the 6-digit code sent to <br />
      <span class="font-black text-[#0B0C0F] tracking-[-0.03em]">{{ formattedMobileNumber }}</span>
    </div>

    <div class="pt-[40px]">
      <div class="w-full flex justify-between mb-[16px]">
        <v-otp-input
          ref="otpInput"
          v-model:value="otpValue"
          :input-classes="['otp-input', { error: hasError }]"
          input-type="number"
          inputmode="numeric"
          separator=" "
          :num-inputs="6"
          :should-auto-focus="true"
          :is-disabled="loading"
          @on-complete="handleNext"
        />
      </div>

      <p v-if="hasError" class="text-[#EC4E20] text-[1rem] leading-[1.5rem] mt-[4px]">
        {{ errorMessage }}
      </p>

      <p
        class="text-[1rem] leading-[1.5rem] mt-[32px] mb-[36px] text-[#000000] text-center"
        v-if="!showResendButton"
      >
        Resend Code in {{ showResendButtonCount }} sec
      </p>

      <p
        class="text-[1rem] leading-[1.5rem] mt-[32px] mb-[36px] text-[#454BEF] text-center underline cursor-pointer"
        v-if="showResendButton"
        @click="resendOtp"
      >
        Resend Code
      </p>
    </div>

    <BottomButton
      text="Next"
      :disabled="loading || isSubmitting"
      :loading="isSubmitting"
      @click="handleNext"
      class="my-[32px]"
    />
  </div>
</template>

<script>
import BottomButton from '@/components/common/BottomButton.vue';
import Toast from '@/components/common/Toast.vue';
import VOtpInput from 'vue3-otp-input';
import { mapState } from 'vuex';

export default {
  components: {
    BottomButton,
    Toast,
    VOtpInput,
  },
  props: {
    countryCode: {
      type: String,
      required: true,
    },
    mobileNumber: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      showResendButton: false,
      showResendButtonCount: 60,
      otpTimer: null,
      otpValue: '',
      hasError: false,
      errorMessage: '',
      isSubmitting: false,
    };
  },
  computed: {
    ...mapState('forgotCredentials', {
      loading: 'isLoading',
    }),
    formattedMobileNumber() {
      return `+${this.countryCode} ${this.mobileNumber}`;
    },
  },
  async mounted() {
    this.startResendTimer();
  },
  beforeUnmount() {
    if (this.otpTimer) {
      clearInterval(this.otpTimer);
    }
  },
  methods: {
    async sendOtp() {
      try {
        await this.$store.dispatch('forgotCredentials/sendMobileOtp', {
          countryCode: this.countryCode,
          contactNumber: this.mobileNumber,
        });
      } catch (error) {
        console.error(error);
      }
    },
    startResendTimer() {
      this.showResendButton = false;
      this.showResendButtonCount = 60;

      this.otpTimer = setInterval(() => {
        this.showResendButtonCount--;
        if (this.showResendButtonCount === 0) {
          this.showResendButton = true;
          clearInterval(this.otpTimer);
        }
      }, 1000);
    },
    async resendOtp() {
      try {
        this.startResendTimer();
        await this.sendOtp();
      } catch (error) {
        console.error(error);
      }
    },
    async handleNext() {
      if (this.otpValue && this.otpValue.length === 6) {
        if (this.isSubmitting) return;
        this.isSubmitting = true;
        this.hasError = false;
        try {
          await this.$store.dispatch('forgotCredentials/verifyMobileOtp', {
            countryCode: this.countryCode,
            contactNumber: this.mobileNumber,
            otp: this.otpValue,
          });
          this.$emit('next');
        } catch (error) {
          this.$refs.otpInput.clearInput();
          // If its invalid input, show as error message below input
          if (error.response?.data?.message?.includes('Incorrect OTP')) {
            this.hasError = true;
            this.errorMessage = 'Wrong OTP entered, Please try again.';
          }
          console.error(error);
        } finally {
          this.isSubmitting = false;
        }
      }
    },
  },
};
</script>

<style scoped>
:deep(.otp-input) {
  width: 48px;
  height: 69px;
  border-radius: 12px;
  border: 1px solid #d8dadc;
  text-align: center;
  background: #f6f4f0;
  font-size: 18px;
  font-weight: 600;
}

:deep(.otp-input.error) {
  border-color: #ec4e20;
  background-color: #fef3f2;
}

:deep(.otp-input.error:focus) {
  border-color: #ec4e20;
  outline: none;
}

:deep(.otp-input:focus) {
  border: 1.1px solid #454bef;
  outline: none;
}

:deep(input::-webkit-outer-spin-button),
:deep(input::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}
</style>
